import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Divider } from '@mui/material';
import { Print, ArrowBack } from '@mui/icons-material';
import { consentFormService, printSettingsService } from '../../services/api';

const ConsentFormPrint = () => {
  const { id: surgeryId, consentId } = useParams();
  const navigate = useNavigate();
  const [consent, setConsent] = useState(null);
  const [printSettings, setPrintSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mark the body so index.css skips its visibility:hidden prescription-print rule.
  useEffect(() => {
    document.body.classList.add('consent-print-page');
    return () => document.body.classList.remove('consent-print-page');
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [cRes, psRes] = await Promise.all([
          consentFormService.getById(surgeryId, consentId),
          printSettingsService.getSettings(),
        ]);
        setConsent(cRes.data);
        setPrintSettings(psRes.data);
      } catch {
        alert('Failed to load consent form');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [surgeryId, consentId]);

  const calculateAge = (dob) => {
    if (!dob) return '';
    const diff = new Date() - new Date(dob);
    return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
  };

  const renderConsentText = (text, surg, ps) => {
    if (!text) return '';
    const patient = surg?.patient;
    const surgeon = surg?.surgeon;
    const age = patient?.dateOfBirth ? calculateAge(patient.dateOfBirth) : '';
    const dt = surg?.scheduledDateTime ? new Date(surg.scheduledDateTime) : null;
    return text
      .replace(/\{\{PatientName\}\}/g,     patient ? `${patient.firstName} ${patient.lastName}` : '___')
      .replace(/\{\{PatientId\}\}/g,       patient?.id ? String(patient.id) : '___')
      .replace(/\{\{PatientAge\}\}/g,      age || '___')
      .replace(/\{\{PatientGender\}\}/g,   patient?.gender || '___')
      .replace(/\{\{PatientAddress\}\}/g,  patient?.address || '___')
      .replace(/\{\{PatientPhone\}\}/g,    patient?.phoneNumber || '___')
      .replace(/\{\{SurgeonName\}\}/g,     surgeon ? `Dr. ${surgeon.firstName} ${surgeon.lastName}` : '___')
      .replace(/\{\{SurgeryDate\}\}/g,     dt ? dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : '___')
      .replace(/\{\{SurgeryTime\}\}/g,     dt ? dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '___')
      .replace(/\{\{OTRoom\}\}/g,          surg?.otRoom?.name || '___')
      .replace(/\{\{SurgeryType\}\}/g,     surg?.surgeryType?.name || '___')
      .replace(/\{\{Icd10Code\}\}/g,       surg?.surgeryType?.icd10Code || '___')
      .replace(/\{\{DiagnosisNotes\}\}/g,  surg?.notes || '___')
      .replace(/\{\{HospitalName\}\}/g,    ps?.hospitalName || 'Vedanta Hospital')
      .replace(/\{\{HospitalAddress\}\}/g, ps?.address || '___');
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
  );

  if (!consent) return <Box sx={{ p: 4 }}><Typography>Consent form not found.</Typography></Box>;

  const { surgery } = consent;
  const ps = printSettings || {};
  const mt = ps.marginTop ?? 10;
  const mb = ps.marginBottom ?? 10;
  const ml = ps.marginLeft ?? 10;
  const mr = ps.marginRight ?? 10;

  const consentTypeLabels = {
    Surgery: 'SURGICAL PROCEDURE CONSENT FORM',
    Anesthesia: 'ANAESTHESIA CONSENT FORM',
    BloodTransfusion: 'BLOOD TRANSFUSION CONSENT FORM',
  };

  const processedBody = renderConsentText(consent.consentText || '', surgery, ps);
  const isHtmlBody = processedBody.trimStart().startsWith('<');

  // Signature footer — shared between screen (inline) and print (fixed).
  const SignatureBlock = () => (
    <>
      {/* 3-column signature row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 28px' }}>

        {/* Patient / Guardian */}
        <Box sx={{ textAlign: 'center' }}>
          {consent.status === 'Signed' && consent.signedAt ? (
            <>
              <Typography sx={{ fontFamily: 'inherit', fontSize: '10px', mb: 0.5 }}>
                Signed on: {new Date(consent.signedAt).toLocaleDateString('en-IN')}
              </Typography>
              <Box sx={{ borderBottom: '1.5px solid #333', mb: 0.5, minHeight: '28px' }} />
            </>
          ) : (
            <Box sx={{ borderBottom: '1.5px solid #333', mb: 0.5, minHeight: '36px' }} />
          )}
          <Typography sx={{ fontFamily: 'inherit', fontSize: '10px', fontWeight: 600 }}>
            {consent.guardianName ? `${consent.guardianName} (Guardian)` : 'Patient Signature'}
          </Typography>
        </Box>

        {/* Witness */}
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ borderBottom: '1.5px solid #333', mb: 0.5, minHeight: '36px' }} />
          <Typography sx={{ fontFamily: 'inherit', fontSize: '10px', fontWeight: 600 }}>
            {consent.witnessName || 'Witness Signature'}
          </Typography>
          <Typography sx={{ fontFamily: 'inherit', fontSize: '9px', color: '#555' }}>(Witness)</Typography>
        </Box>

        {/* Doctor */}
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ borderBottom: '1.5px solid #333', mb: 0.5, minHeight: '36px' }} />
          <Typography sx={{ fontFamily: 'inherit', fontSize: '10px', fontWeight: 600 }}>
            {surgery?.surgeon ? `Dr. ${surgery.surgeon.firstName} ${surgery.surgeon.lastName}` : "Surgeon's Signature"}
          </Typography>
          {surgery?.surgeon?.specialization && (
            <Typography sx={{ fontFamily: 'inherit', fontSize: '9px', color: '#555' }}>
              {surgery.surgeon.specialization}
            </Typography>
          )}
          {surgery?.surgeon?.medicalRegistrationNumber && (
            <Typography sx={{ fontFamily: 'inherit', fontSize: '9px', color: '#555' }}>
              Reg. No: {surgery.surgeon.medicalRegistrationNumber}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Guardian note */}
      {(consent.guardianName || consent.guardianRelation) && (
        <Typography sx={{ fontFamily: 'inherit', fontSize: '10px', mt: 0.75 }}>
          <strong>Note:</strong> Consent provided by legal guardian/representative.&nbsp;
          <strong>Guardian:</strong> {consent.guardianName}&nbsp;&nbsp;
          <strong>Relation:</strong> {consent.guardianRelation}
        </Typography>
      )}

      {/* Date & Place */}
      <Box sx={{ mt: 1, display: 'flex', gap: 5 }}>
        <Typography sx={{ fontFamily: 'inherit', fontSize: '11px' }}>
          <strong>Date:</strong> {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
        </Typography>
        <Typography sx={{ fontFamily: 'inherit', fontSize: '11px' }}>
          <strong>Place:</strong> ___________________________
        </Typography>
      </Box>
    </>
  );

  return (
    <Box>
      {/* Print controls — hidden when printing */}
      <Box className="no-print" sx={{ p: 2, display: 'flex', gap: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>Back</Button>
        <Button variant="contained" startIcon={<Print />} onClick={() => window.print()}>Print</Button>
        <Box sx={{
          ml: 1, px: 1.5, py: 0.4, borderRadius: 1, fontSize: '0.78rem', fontWeight: 600,
          bgcolor: consent.status === 'Signed' ? '#e8f5e9' : '#fff8e1',
          color: consent.status === 'Signed' ? '#2e7d32' : '#f57f17',
          display: 'flex', alignItems: 'center',
        }}>
          {consent.status}
        </Box>
      </Box>

      {/* ── Printable document ── */}
      <Box sx={{
        maxWidth: '210mm',
        margin: '0 auto',
        p: `${mt}mm ${mr}mm ${mb}mm ${ml}mm`,
        fontFamily: '"Times New Roman", serif',
        fontSize: '13px',
        lineHeight: 1.6,
        bgcolor: 'white',
        '@media print': {
          margin: 0,
          maxWidth: 'none',
          padding: `${mt}mm ${mr}mm ${mb}mm ${ml}mm`,
          boxShadow: 'none',
        },
      }}>

        {/* Hospital header — only for non-preprinted letterheads */}
        {ps.letterheadType !== 'Pre-Printed Letterhead' && (
          <>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography sx={{ fontSize: '18px', fontWeight: 700, fontFamily: 'inherit' }}>
                {ps.hospitalName || 'Vedanta Hospital'}
              </Typography>
              {ps.address && <Typography sx={{ fontSize: '12px', fontFamily: 'inherit' }}>{ps.address}</Typography>}
              {ps.phone && <Typography sx={{ fontSize: '12px', fontFamily: 'inherit' }}>Tel: {ps.phone}</Typography>}
            </Box>
            <Divider sx={{ mb: 2, borderColor: '#333', borderWidth: 1 }} />
          </>
        )}

        {/* Form title — screen only; the template body already contains the heading */}
        <Box className="consent-print-hide" sx={{ textAlign: 'center', mb: 3 }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 700, textDecoration: 'underline', fontFamily: 'inherit', letterSpacing: 0.5 }}>
            {consentTypeLabels[consent.consentType] || consent.consentType?.toUpperCase() || 'CONSENT FORM'}
          </Typography>
        </Box>

        {/* Patient details — screen only; the template body carries all patient info via placeholders */}
        <Box className="consent-print-hide" sx={{ mb: 3, p: 1.5, border: '1px solid #ccc' }}>
          <Typography sx={{ fontWeight: 700, mb: 1, fontFamily: 'inherit' }}>PATIENT DETAILS</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px' }}>
            <Typography sx={{ fontFamily: 'inherit', fontSize: '13px' }}>
              <strong>Name:</strong> {surgery?.patient ? `${surgery.patient.firstName} ${surgery.patient.lastName}` : '___________________'}
            </Typography>
            <Typography sx={{ fontFamily: 'inherit', fontSize: '13px' }}>
              <strong>Age / Gender:</strong> {surgery?.patient?.dateOfBirth ? calculateAge(surgery.patient.dateOfBirth) : '___'} yrs / {surgery?.patient?.gender || '___'}
            </Typography>
            <Typography sx={{ fontFamily: 'inherit', fontSize: '13px' }}>
              <strong>Address:</strong> {surgery?.patient?.address || '___________________'}
            </Typography>
            <Typography sx={{ fontFamily: 'inherit', fontSize: '13px' }}>
              <strong>Phone:</strong> {surgery?.patient?.phoneNumber || '___________________'}
            </Typography>
            <Typography sx={{ fontFamily: 'inherit', fontSize: '13px' }}>
              <strong>Procedure:</strong> {surgery?.surgeryName || '___________________'}
            </Typography>
            <Typography sx={{ fontFamily: 'inherit', fontSize: '13px' }}>
              <strong>Scheduled Date:</strong>{' '}
              {surgery?.scheduledDateTime
                ? new Date(surgery.scheduledDateTime).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })
                : '___________________'}
            </Typography>
            <Typography sx={{ fontFamily: 'inherit', fontSize: '13px' }}>
              <strong>Surgeon:</strong>{' '}
              {surgery?.surgeon ? `Dr. ${surgery.surgeon.firstName} ${surgery.surgeon.lastName}` : '___________________'}
            </Typography>
            {surgery?.otRoom && (
              <Typography sx={{ fontFamily: 'inherit', fontSize: '13px' }}>
                <strong>OT Room:</strong> {surgery.otRoom.name}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Consent text body */}
        <Box sx={{ mb: 3 }}>
          {isHtmlBody ? (
            <Box
              dangerouslySetInnerHTML={{ __html: processedBody }}
              sx={{
                fontFamily: 'inherit', fontSize: '13px', textAlign: 'justify',
                '& p': { marginTop: 0, marginBottom: '0.6em' },
                '& ol, & ul': { paddingLeft: '1.5em', marginTop: 0, marginBottom: '0.6em' },
                '& li': { marginBottom: '0.25em' },
                '& h1, & h2, & h3': { fontFamily: 'inherit', marginTop: '0.5em', marginBottom: '0.3em' },
                '& hr': { border: 'none', borderTop: '1px solid #999', margin: '10px 0', pageBreakAfter: 'avoid' },
                '& span[style*="font-size"]': { lineHeight: 1.4 },
              }}
            />
          ) : (
            <Typography sx={{ fontFamily: 'inherit', fontSize: '13px', whiteSpace: 'pre-wrap', textAlign: 'justify' }}>
              {processedBody}
            </Typography>
          )}
        </Box>

        {/* Signature section — appears at the bottom of the last page in both screen and print */}
        <Box sx={{ pt: 2, borderTop: '1.5px solid #333' }}>
          <SignatureBlock />
        </Box>

      </Box>{/* end printable document */}

      <style>{`
        @page {
          size: A4;
          margin: 0;
        }
        @media print {
          html, body, #root {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            height: auto !important;
            min-height: 0 !important;
          }
          .no-print           { display: none !important; }
          .consent-print-hide { display: none !important; }
        }
      `}</style>
    </Box>
  );
};

export default ConsentFormPrint;
